import { usePostHog } from "posthog-js/react";
import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { envConfig } from "@/lib/config";

interface MarketingAnalyticsContextType {
  isInitialized: boolean;
  trackEvent: (event: string, properties?: Record<string, any>) => void;
  trackPageView: (page?: string, properties?: Record<string, any>) => void;
  identifyUser: (userId: string, properties?: Record<string, any>) => void;
}

const MarketingAnalyticsContext = createContext<
  MarketingAnalyticsContextType | undefined
>(undefined);

interface MarketingAnalyticsProviderProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    lintrk?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    Cookiebot?: {
      consent: {
        marketing: boolean;
        statistics: boolean;
        preferences: boolean;
        necessary: boolean;
      };
    };
  }
}

export function MarketingAnalyticsProvider({
  children,
}: MarketingAnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();
  const posthog = usePostHog();

  useEffect(() => {
    // Initialize tracking after a short delay to ensure scripts are loaded
    const timer = setTimeout(() => {
      setIsInitialized(true);

      // Identify user if authenticated
      if (user?.id) {
        identifyUser(user.id, {
          email: user.email,
          created_at: user.created_at,
        });
      }
    }, 1000);

    // Listen for Cookiebot consent events
    const handleCookiebotEvent = () => {
      if (window.Cookiebot) {
        console.log(
          "Cookiebot event, consent status:",
          window.Cookiebot.consent
        );
      }
    };

    // Listen for custom cookie consent events (fallback)
    const handleCustomConsent = (event: CustomEvent) => {
      console.log("Custom cookie consent:", event.type);
    };

    // Cookiebot events
    window.addEventListener("CookiebotOnLoad", handleCookiebotEvent);
    window.addEventListener("CookiebotOnAccept", handleCookiebotEvent);
    window.addEventListener("CookiebotOnDecline", handleCookiebotEvent);

    // Custom consent events (fallback)
    window.addEventListener(
      "cookies-accepted",
      handleCustomConsent as EventListener
    );
    window.addEventListener(
      "cookies-declined",
      handleCustomConsent as EventListener
    );

    return () => {
      clearTimeout(timer);
      window.removeEventListener("CookiebotOnLoad", handleCookiebotEvent);
      window.removeEventListener("CookiebotOnAccept", handleCookiebotEvent);
      window.removeEventListener("CookiebotOnDecline", handleCookiebotEvent);
      window.removeEventListener(
        "cookies-accepted",
        handleCustomConsent as EventListener
      );
      window.removeEventListener(
        "cookies-declined",
        handleCustomConsent as EventListener
      );
    };
  }, [user]);

  const trackEvent = (event: string, properties: Record<string, any> = {}) => {
    if (!isInitialized) return;

    console.log("Marketing Analytics Event:", event, properties);

    // Check consent (Cookiebot or fallback to localStorage)
    let hasMarketingConsent = true;
    let hasStatisticsConsent = true;

    if (window.Cookiebot) {
      hasMarketingConsent = window.Cookiebot.consent.marketing;
      hasStatisticsConsent = window.Cookiebot.consent.statistics;
    } else {
      // Fallback to custom consent
      const consent = localStorage.getItem("cookie-consent");
      hasMarketingConsent = consent === "accepted";
      hasStatisticsConsent = consent === "accepted";
    }

    // Google Tag Manager / Google Analytics
    if (window.gtag && hasStatisticsConsent && envConfig.ANALYTICS.GTM_ID) {
      window.gtag("event", event, properties);
    }

    // LinkedIn Insight Tag
    if (window.lintrk && hasMarketingConsent) {
      window.lintrk("track", { conversion_id: event, ...properties });
    }

    // Facebook Pixel
    if (window.fbq && hasMarketingConsent) {
      window.fbq("track", event, properties);
    }

    // PostHog
    posthog?.capture(event, properties);
  };

  const trackPageView = (
    page?: string,
    properties: Record<string, any> = {}
  ) => {
    if (!isInitialized) return;

    const pageData = {
      page_path: page || window.location.pathname,
      page_title: document.title,
      ...properties,
    };

    console.log("Marketing Analytics Page View:", pageData);

    // Check consent (Cookiebot or fallback to localStorage)
    let hasMarketingConsent = true;
    let hasStatisticsConsent = true;

    if (window.Cookiebot) {
      hasMarketingConsent = window.Cookiebot.consent.marketing;
      hasStatisticsConsent = window.Cookiebot.consent.statistics;
    } else {
      // Fallback to custom consent
      const consent = localStorage.getItem("cookie-consent");
      hasMarketingConsent = consent === "accepted";
      hasStatisticsConsent = consent === "accepted";
    }

    // Google Tag Manager / Google Analytics
    if (window.gtag && hasStatisticsConsent) {
      window.gtag("config", envConfig.ANALYTICS.GTM_ID, pageData);
    }

    // Facebook Pixel
    if (window.fbq && hasMarketingConsent) {
      window.fbq("track", "PageView", pageData);
    }

    // LinkedIn tracking happens automatically with their script

    // PostHog pageview
    posthog?.capture("$pageview", pageData);
  };

  const identifyUser = (
    userId: string,
    properties: Record<string, any> = {}
  ) => {
    if (!isInitialized) return;

    console.log("Marketing Analytics User Identified:", userId, properties);

    // Google Tag Manager / Google Analytics
    if (window.gtag) {
      window.gtag("config", envConfig.ANALYTICS.GTM_ID, {
        user_id: userId,
        custom_map: properties,
      });
    }

    // Facebook Pixel - User identification
    if (window.fbq) {
      window.fbq("init", envConfig.ANALYTICS.FACEBOOK_PIXEL_ID, {
        em: properties.email, // Email for enhanced matching
      });
    }

    // PostHog identify
    posthog?.identify(userId, properties);
  };

  const value = {
    isInitialized,
    trackEvent,
    trackPageView,
    identifyUser,
  };

  return (
    <MarketingAnalyticsContext.Provider value={value}>
      {children}
    </MarketingAnalyticsContext.Provider>
  );
}

export function useMarketingAnalytics() {
  const context = useContext(MarketingAnalyticsContext);
  if (context === undefined) {
    throw new Error(
      "useMarketingAnalytics must be used within a MarketingAnalyticsProvider"
    );
  }
  return context;
}
